import type { ReactNode } from "react";
import { BasePanel } from "~/components/base-panel";
import { Button, ButtonVariants } from "~/components/button";
import { EmptyPanel } from "~/components/empty/empty-panel";
import { Icon, IconProps } from "~/components/icon/icon";

const panelClasses = { text: "max-w-md", container: "bg-gray-50 py-16" };

type StateTone = "success" | "warning" | "neutral";

const stateImage = (tone: StateTone, icon?: IconProps["name"]) => {
  if (tone === "success") {
    return (
      <Icon
        name="circle-check"
        size="large"
        iconClassName="text-system-success"
      />
    );
  }

  if (tone === "warning") {
    return (
      <Icon
        name={icon ?? "triangle-alert"}
        size="large"
        iconClassName="text-system-error"
      />
    );
  }

  return (
    <div className="flex size-10 items-center justify-center rounded-full bg-secondary-100">
      <Icon
        name={icon ?? "circle-question-mark"}
        size="medium"
        iconClassName="text-secondary-400"
      />
    </div>
  );
};

export const actionButton = ({
  href,
  label,
  testId,
  variant,
  icon,
}: {
  href: string | null;
  label: string;
  testId: string;
  variant: ButtonVariants;
  icon: IconProps["name"];
}) => {
  if (href) {
    return (
      <Button
        key={testId}
        asChild
        variant={variant}
        size="small"
        icon={icon}
        testId={testId}
      >
        <a href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      </Button>
    );
  }

  // Cursor on the wrapper: Button stays pointer-events-none so disabled hover never fires.
  return (
    <span key={testId} className="inline-flex cursor-not-allowed">
      <Button
        variant={variant}
        size="small"
        icon={icon}
        testId={testId}
        disabled
      >
        {label}
      </Button>
    </span>
  );
};

interface StatePanelProps {
  testId: string;
  tone: StateTone;
  icon?: IconProps["name"];
  title: string;
  text: string;
  buttons?: ReactNode[];
  footer?: ReactNode;
}

export const statePanel = ({
  testId,
  tone,
  icon,
  title,
  text,
  buttons,
  footer,
}: StatePanelProps) => (
  <BasePanel
    testId={testId}
    isEmpty
    emptyComponent={
      <EmptyPanel
        image={stateImage(tone, icon)}
        title={title}
        text={
          footer ? (
            <div className="flex flex-col items-center gap-3">
              <span>{text}</span>
              {footer}
            </div>
          ) : (
            text
          )
        }
        classes={panelClasses}
        buttons={buttons}
      />
    }
  />
);
